/**
 * Tier-Aware Loading Quips for Narrator System
 *
 * These quips display during analysis loading, matching the tier's voice profile.
 * They serve as a bridge until the real LLM narrator_intro arrives via streaming.
 *
 * Tier Voice Profiles:
 * - L1 (Free/Anonymous): 80% playful - Witty, casual, TV-show framing
 * - L2 (Drama Plus): 50% balanced - Approachable expert
 * - L3 (DramaX): 20% professional - Analytical, concise
 *
 * Created: 2026-02-03
 */

import type { TierId } from '@dramadetector/shared';

/**
 * Re-export canonical `TierId` as `UserTier` for clearer naming in narrator context.
 * Maps to: 'anonymous' | 'free' | 'drama_plus' | 'dramax'
 */
export type UserTier = TierId;

export interface LoadingQuip {
  /** Unique identifier for this quip */
  id: string;
  /** i18n translation key (e.g., 'loading.narrator_quips.l1.quip1') */
  i18nKey: string;
  /** Fallback text if translation not found */
  fallbackText: string;
  /** Duration to show this quip in ms (before cycling to next) */
  duration: number;
}

export interface TierQuipConfig {
  /** Quips cycle through these during loading */
  quips: LoadingQuip[];
  /** Cycle interval in ms */
  cycleInterval: number;
  /** Voice style descriptor for UI/analytics */
  voiceStyle: 'playful' | 'balanced' | 'professional';
}

/**
 * L1 Tier Quips (Free/Anonymous)
 * Voice: 80% playful - Witty, casual, TV-show framing
 * Uses i18n keys from loading.narrator_quips.l1
 */
const L1_QUIPS: LoadingQuip[] = [
  { id: 'l1_1', i18nKey: 'loading.narrator_quips.l1.quip1', fallbackText: 'Grabbing the popcorn...', duration: 2000 },
  { id: 'l1_2', i18nKey: 'loading.narrator_quips.l1.quip2', fallbackText: 'Episode loading...', duration: 2000 },
  { id: 'l1_3', i18nKey: 'loading.narrator_quips.l1.quip3', fallbackText: 'Reading between the lines...', duration: 2000 },
  { id: 'l1_4', i18nKey: 'loading.narrator_quips.l1.quip4', fallbackText: 'Plot twist incoming...', duration: 2000 },
  { id: 'l1_5', i18nKey: 'loading.narrator_quips.l1.quip5', fallbackText: 'Checking the drama levels...', duration: 2000 },
  { id: 'l1_6', i18nKey: 'loading.narrator_quips.l1.quip6', fallbackText: 'Cue the dramatic music...', duration: 2000 },
  { id: 'l1_7', i18nKey: 'loading.narrator_quips.l1.quip7', fallbackText: 'Reviewing the script...', duration: 2000 },
  { id: 'l1_8', i18nKey: 'loading.narrator_quips.l1.quip8', fallbackText: 'This is getting good...', duration: 2000 },
];

/**
 * L2 Tier Quips (Drama Plus)
 * Voice: 50% balanced - Approachable expert
 * Uses i18n keys from loading.narrator_quips.l2
 */
const L2_QUIPS: LoadingQuip[] = [
  { id: 'l2_1', i18nKey: 'loading.narrator_quips.l2.quip1', fallbackText: 'Analyzing communication patterns...', duration: 2500 },
  { id: 'l2_2', i18nKey: 'loading.narrator_quips.l2.quip2', fallbackText: 'Looking at the dynamics here...', duration: 2500 },
  { id: 'l2_3', i18nKey: 'loading.narrator_quips.l2.quip3', fallbackText: 'Processing the context...', duration: 2500 },
  { id: 'l2_4', i18nKey: 'loading.narrator_quips.l2.quip4', fallbackText: 'Evaluating tone and intent...', duration: 2500 },
  { id: 'l2_5', i18nKey: 'loading.narrator_quips.l2.quip5', fallbackText: 'Mapping the conversation flow...', duration: 2500 },
  { id: 'l2_6', i18nKey: 'loading.narrator_quips.l2.quip6', fallbackText: 'Identifying key themes...', duration: 2500 },
];

/**
 * L3 Tier Quips (DramaX)
 * Voice: 20% professional - Analytical, concise
 * Uses i18n keys from loading.narrator_quips.l3
 */
const L3_QUIPS: LoadingQuip[] = [
  { id: 'l3_1', i18nKey: 'loading.narrator_quips.l3.quip1', fallbackText: 'Running analysis...', duration: 3000 },
  { id: 'l3_2', i18nKey: 'loading.narrator_quips.l3.quip2', fallbackText: 'Processing message...', duration: 3000 },
  { id: 'l3_3', i18nKey: 'loading.narrator_quips.l3.quip3', fallbackText: 'Evaluating patterns...', duration: 3000 },
  { id: 'l3_4', i18nKey: 'loading.narrator_quips.l3.quip4', fallbackText: 'Analyzing data...', duration: 3000 },
];

/**
 * Tier-specific quip configurations
 *
 * Maps each UserTier to its quip pool, cycle interval, and voice style.
 * Anonymous and Free share L1 (playful) quips.
 */
export const TIER_QUIP_CONFIG: Record<UserTier, TierQuipConfig> = {
  anonymous: {
    quips: L1_QUIPS,
    cycleInterval: 2000,
    voiceStyle: 'playful',
  },
  free: {
    quips: L1_QUIPS,
    cycleInterval: 2000,
    voiceStyle: 'playful',
  },
  drama_plus: {
    quips: L2_QUIPS,
    cycleInterval: 2500,
    voiceStyle: 'balanced',
  },
  dramax: {
    quips: L3_QUIPS,
    cycleInterval: 3000,
    voiceStyle: 'professional',
  },
};

/**
 * Get quip configuration for a user tier
 *
 * @param tier - User's subscription tier
 * @returns TierQuipConfig with quips, cycle interval, and voice style
 */
export function getQuipConfigForTier(tier: UserTier): TierQuipConfig {
  return TIER_QUIP_CONFIG[tier] || TIER_QUIP_CONFIG.free;
}

/**
 * Get a random quip for a tier (for initial display)
 *
 * @param tier - User's subscription tier
 * @returns Random LoadingQuip from the tier's quip pool
 */
export function getRandomQuipForTier(tier: UserTier): LoadingQuip {
  const config = getQuipConfigForTier(tier);
  const randomIndex = Math.floor(Math.random() * config.quips.length);
  return config.quips[randomIndex];
}

/**
 * Get the next quip in sequence (for cycling)
 *
 * @param tier - User's subscription tier
 * @param currentIndex - Current quip index
 * @returns Next quip index (wraps around)
 */
export function getNextQuipIndex(tier: UserTier, currentIndex: number): number {
  const config = getQuipConfigForTier(tier);
  return (currentIndex + 1) % config.quips.length;
}

/**
 * Get quip count for a tier
 *
 * @param tier - User's subscription tier
 * @returns Number of quips available for this tier
 */
export function getQuipCountForTier(tier: UserTier): number {
  const config = getQuipConfigForTier(tier);
  return config.quips.length;
}

/**
 * Get translated quip text
 *
 * Uses the provided translation function to get localized text,
 * falling back to the English fallbackText if translation not found.
 *
 * @param quip - The loading quip
 * @param t - Translation function from useTranslation
 * @returns Translated quip text
 */
export function getQuipText(
  quip: LoadingQuip,
  t: (key: string, options?: { defaultValue?: string }) => string
): string {
  return t(quip.i18nKey, { defaultValue: quip.fallbackText });
}
